import ContentBody from 'components/content-body/content-body';
import ContentHeaderTitle from 'components/content-header-title/content-header-title';
import Content from 'components/content/content';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';
import React, { useMemo } from 'react';
import { Button, Divider, Loader, Message } from 'semantic-ui-react';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from 'use-query-params';
import { ManageNetworkMap } from './manage-network-map';
import { useProViewsCount } from 'hooks/use-pro-views-count/use-pro-views-count';
import { ProViewBoolExp } from 'generated/graphql';
import ContentHeader from 'components/content-header/content-header';
import { TradeSearchFilter } from 'components/trade-search-filter';
import CheckboxFilter from 'components/checkbox-filter/checkbox-filter';

const ManageNetworkPage: React.FunctionComponent = () => {
  const { currentBilikZones } = useCurrentBilikZones();

  const [filters, setFilters] = useQueryParams({
    tradeId: NumberParam,
    onlyPresentationCategories: BooleanParam,
    status: StringParam,
    displayHeatmap: BooleanParam,
  });

  const where = useMemo(() => {
    const where: ProViewBoolExp = {
      bilikZoneId: { _in: currentBilikZones?.map((zone) => zone.id) },
    };

    if (filters.tradeId && !filters.onlyPresentationCategories) {
      where.trades = {};
      where.trades.tradeId = { _eq: filters.tradeId };
    }

    if (filters.onlyPresentationCategories && filters.tradeId) {
      where.proPresentation = {};
      where.proPresentation.mainTrades = {};
      where.proPresentation.mainTrades.tradeId = { _eq: filters.tradeId };
    }

    return where;
  }, [filters.tradeId, filters.onlyPresentationCategories, currentBilikZones]);

  const { count: countIsStatusPublished, loading: loadingIsStatusPublished } =
    useProViewsCount({
      ...where,
      status: { _eq: 'published' },
    });

  const { count: countIsStatusDraft, loading: loadingIsStatusDraft } =
    useProViewsCount({
      ...where,
      status: { _eq: 'draft' },
    });

  if (currentBilikZones === undefined) {
    return (
      <Loader
        style={{ marginTop: '50px' }}
        size="large"
        active
        inline="centered"
      >
        Chargement...
      </Loader>
    );
  }

  return (
    <Content>
      <ContentHeader>
        <ContentHeaderTitle>
          Piloter mon réseau{' '}
          <small>
            {currentBilikZones
              ? currentBilikZones.map((zone) => zone.mainCityCode).join(', ')
              : 'Toutes les zones'}
          </small>
        </ContentHeaderTitle>
      </ContentHeader>
      <ContentBody>
        {!currentBilikZones?.length || currentBilikZones.length > 4 ? (
          <Message error>
            Le tableau de bord ne peut s&apos;afficher que lorsqu&apos;au moins
            1 zone est sélectionnée (4 zones max)
          </Message>
        ) : (
          <>
            <Button.Group>
              <Button
                data-ga-event="select_content"
                data-ga-category="manage_network_filters"
                data-ga-label="status_published"
                basic={filters.status !== 'published'}
                color="green"
                loading={loadingIsStatusPublished}
                onClick={(): void => {
                  setFilters({
                    status: 'published',
                  });
                }}
              >
                Publiés ({countIsStatusPublished ?? 0})
              </Button>
              <Button
                data-ga-event="select_content"
                data-ga-category="manage_network_filters"
                data-ga-label="status_draft"
                basic={filters.status !== 'draft'}
                color="red"
                loading={loadingIsStatusDraft}
                onClick={(): void => {
                  setFilters({
                    status: 'draft',
                  });
                }}
              >
                Brouillon ({countIsStatusDraft ?? 0})
              </Button>
              <Button
                basic={filters.status !== undefined}
                color="grey"
                onClick={(): void => {
                  setFilters({
                    status: undefined,
                  });
                }}
              >
                Tous (
                {(countIsStatusPublished ?? 0) + (countIsStatusDraft ?? 0)})
              </Button>
            </Button.Group>
            <TradeSearchFilter
              data-ga-event="select_content"
              data-ga-category="manage_network_filters"
              data-ga-label="trade_select"
              tradeId={filters.tradeId || undefined}
              onDelete={(): void => {
                setFilters({
                  tradeId: undefined,
                });
              }}
              onResultSelect={(event, { result }): void => {
                setFilters({
                  tradeId: result.id,
                });
              }}
            />
            {filters.tradeId && (
              <CheckboxFilter
                data-ga-event="select_content"
                data-ga-category="manage_network_filters"
                data-ga-label="only_presentation_categories_checkbox"
                label="Catégories principales uniquement"
                className="button basic grey"
                checked={filters.onlyPresentationCategories || false}
                onChange={(): void => {
                  setFilters({
                    onlyPresentationCategories:
                      !filters.onlyPresentationCategories,
                  });
                }}
              />
            )}
            {filters.tradeId && (
              <CheckboxFilter
                data-ga-event="select_content"
                data-ga-category="manage_network_filters"
                data-ga-label="display_heatmap_checkbox"
                label="Afficher la heatmap"
                className="button basic grey"
                checked={filters.displayHeatmap || false}
                onChange={(): void => {
                  setFilters({
                    displayHeatmap: !filters.displayHeatmap,
                  });
                }}
              />
            )}

            <Divider hidden />
            <ManageNetworkMap
              filters={{
                zoneIds: currentBilikZones.map((zone) => zone.id),
                tradeId: filters.tradeId ?? undefined,
                onlyPresentationCategories:
                  filters.onlyPresentationCategories ?? false,
                status: filters.status as 'published' | 'draft' | undefined,
                displayHeatmap: filters.displayHeatmap ?? false,
              }}
            />
          </>
        )}
      </ContentBody>
    </Content>
  );
};

export default ManageNetworkPage;
